import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type SetValue<T> = T | ((current: T) => T);

export interface QueryFilterCodec<T> {
  readonly defaultValue: T;
  readonly param?: string;
  parse(values: string[]): T;
  serialize(value: T): string[];
  equals?(left: T, right: T): boolean;
}

export type QueryFilterSchema = Record<string, QueryFilterCodec<unknown>>;

export type QueryFilterValues<TSchema extends QueryFilterSchema> = {
  [TKey in keyof TSchema]: TSchema[TKey] extends QueryFilterCodec<infer TValue>
    ? TValue
    : never;
};

export interface QueryFilterUpdateOptions {
  /**
   * Replaces the current history entry by default so typing does not create one
   * browser-history entry per character. Route navigation still preserves the
   * complete URL, so Back/Forward restore the listing exactly.
   */
  replace?: boolean;
  /** Reset the configured page param when a non-page filter changes. */
  resetPage?: boolean;
}

interface UseQueryFiltersOptions<TSchema extends QueryFilterSchema> {
  pageKey?: keyof TSchema;
  replace?: boolean;
}

const normalizeArray = (values: string[]): string[] => (
  values
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean)
);

const areArraysEqual = <T,>(left: readonly T[], right: readonly T[]): boolean => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const defaultEquals = <T,>(left: T, right: T): boolean => {
  if (Array.isArray(left) && Array.isArray(right)) {
    return areArraysEqual(left, right);
  }

  return Object.is(left, right);
};

const resolveParamName = (property: string, codec: QueryFilterCodec<unknown>): string => (
  codec.param ?? property
);

const readValues = <TSchema extends QueryFilterSchema>(
  searchParams: URLSearchParams,
  schema: TSchema,
): QueryFilterValues<TSchema> => {
  const result: Partial<QueryFilterValues<TSchema>> = {};

  Object.entries(schema).forEach(([property, codec]) => {
    const param = resolveParamName(property, codec);
    result[property as keyof TSchema] = codec.parse(searchParams.getAll(param)) as QueryFilterValues<TSchema>[keyof TSchema];
  });

  return result as QueryFilterValues<TSchema>;
};

const writeValue = (
  searchParams: URLSearchParams,
  property: string,
  codec: QueryFilterCodec<unknown>,
  value: unknown,
): void => {
  const param = resolveParamName(property, codec);
  searchParams.delete(param);

  const equals = codec.equals ?? defaultEquals;
  if (equals(value, codec.defaultValue)) {
    return;
  }

  codec.serialize(value).forEach((serialized) => {
    if (serialized) {
      searchParams.append(param, serialized);
    }
  });
};

const writeValueIfChanged = (
  searchParams: URLSearchParams,
  property: string,
  codec: QueryFilterCodec<unknown>,
  value: unknown,
): void => {
  const param = resolveParamName(property, codec);
  const equals = codec.equals ?? defaultEquals;
  const serialized = equals(value, codec.defaultValue)
    ? []
    : codec.serialize(value).filter(Boolean);

  if (areArraysEqual(searchParams.getAll(param), serialized)) {
    return;
  }

  searchParams.delete(param);
  serialized.forEach((item) => searchParams.append(param, item));
};

export const queryFilter = {
  string(defaultValue = '', options: { param?: string; trim?: boolean } = {}): QueryFilterCodec<string> {
    // trim defaults to false so search inputs can keep trailing spaces while typing
    // (e.g. "Maria " → "Maria Silva"). Opt in with `{ trim: true }` when needed.
    const shouldTrim = options.trim === true;

    return {
      defaultValue,
      param: options.param,
      parse: (values) => {
        const value = values[0] ?? defaultValue;
        return shouldTrim ? value.trim() : value;
      },
      serialize: (value) => {
        const normalized = shouldTrim ? value.trim() : value;
        return normalized ? [normalized] : [];
      },
    };
  },

  integer(
    defaultValue: number,
    options: {
      param?: string;
      min?: number;
      max?: number;
      allowed?: readonly number[];
    } = {},
  ): QueryFilterCodec<number> {
    return {
      defaultValue,
      param: options.param,
      parse: (values) => {
        const parsed = Number.parseInt(values[0] ?? '', 10);
        if (!Number.isInteger(parsed)) return defaultValue;
        if (options.min !== undefined && parsed < options.min) return defaultValue;
        if (options.max !== undefined && parsed > options.max) return defaultValue;
        if (options.allowed && !options.allowed.includes(parsed)) return defaultValue;
        return parsed;
      },
      serialize: (value) => [String(value)],
    };
  },

  boolean(defaultValue = false, options: { param?: string } = {}): QueryFilterCodec<boolean> {
    return {
      defaultValue,
      param: options.param,
      parse: (values) => {
        const value = values[0]?.toLowerCase();
        if (value === 'true' || value === '1') return true;
        if (value === 'false' || value === '0') return false;
        return defaultValue;
      },
      serialize: (value) => [value ? 'true' : 'false'],
    };
  },

  enum<TValue extends string>(
    defaultValue: TValue,
    allowed: readonly TValue[],
    options: { param?: string } = {},
  ): QueryFilterCodec<TValue> {
    return {
      defaultValue,
      param: options.param,
      parse: (values) => {
        const value = values[0] as TValue | undefined;
        return value && allowed.includes(value) ? value : defaultValue;
      },
      serialize: (value) => [value],
    };
  },

  array<TValue extends string = string>(
    defaultValue: readonly TValue[] = [],
    options: {
      param?: string;
      allowed?: readonly TValue[];
      /** When default is non-empty, this token in the URL means an explicit empty selection. */
      emptyToken?: string;
    } = {},
  ): QueryFilterCodec<TValue[]> {
    const emptyToken = options.emptyToken?.trim() || null;

    return {
      defaultValue: [...defaultValue],
      param: options.param,
      parse: (values) => {
        if (values.length === 0) {
          return [...defaultValue];
        }

        if (
          emptyToken
          && defaultValue.length > 0
          && values.length === 1
          && values[0]?.trim() === emptyToken
        ) {
          return [];
        }

        const normalized = normalizeArray(values) as TValue[];
        const filtered = options.allowed
          ? normalized.filter((value) => options.allowed?.includes(value))
          : normalized;
        return Array.from(new Set(filtered));
      },
      serialize: (value) => {
        if (value.length === 0 && emptyToken && defaultValue.length > 0) {
          return [emptyToken];
        }

        return Array.from(new Set(value.map((item) => item.trim()).filter(Boolean)));
      },
      equals: areArraysEqual,
    };
  },
};

/**
 * Keeps listing state exclusively in URL query params.
 *
 * Defaults are represented by an absent param, while every non-default value
 * is serialized. This makes refresh, copied URLs and browser Back/Forward
 * deterministic without a second React/localStorage source of truth.
 */
export const useQueryFilters = <TSchema extends QueryFilterSchema>(
  schema: TSchema,
  options: UseQueryFiltersOptions<TSchema> = {},
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const values = useMemo(
    () => readValues(searchParams, schema),
    [schema, searchKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setSearchParams((currentParams) => {
      const canonicalParams = new URLSearchParams(currentParams);
      const currentValues = readValues(currentParams, schema);

      Object.entries(schema).forEach(([property, codec]) => {
        writeValueIfChanged(canonicalParams, property, codec, currentValues[property]);
      });

      return canonicalParams.toString() === currentParams.toString()
        ? currentParams
        : canonicalParams;
    }, { replace: true });
  }, [schema, searchKey, setSearchParams]);

  const updateParams = useCallback((
    updater: (
      currentValues: QueryFilterValues<TSchema>,
      nextParams: URLSearchParams,
    ) => void,
    updateOptions: QueryFilterUpdateOptions = {},
  ) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      const currentValues = readValues(currentParams, schema);
      updater(currentValues, nextParams);
      return nextParams;
    }, {
      replace: updateOptions.replace ?? options.replace ?? true,
    });
  }, [options.replace, schema, setSearchParams]);

  const setFilter = useCallback(<TKey extends keyof TSchema>(
    key: TKey,
    nextValue: SetValue<QueryFilterValues<TSchema>[TKey]>,
    updateOptions: QueryFilterUpdateOptions = {},
  ) => {
    updateParams((currentValues, nextParams) => {
      const property = String(key);
      const codec = schema[key];
      const resolvedValue = typeof nextValue === 'function'
        ? (nextValue as (current: QueryFilterValues<TSchema>[TKey]) => QueryFilterValues<TSchema>[TKey])(currentValues[key])
        : nextValue;

      writeValue(nextParams, property, codec, resolvedValue);

      const pageKey = options.pageKey;
      if (
        pageKey
        && key !== pageKey
        && updateOptions.resetPage !== false
      ) {
        const pageCodec = schema[pageKey];
        writeValue(nextParams, String(pageKey), pageCodec, pageCodec.defaultValue);
      }
    }, updateOptions);
  }, [options.pageKey, schema, updateParams]);

  const setFilters = useCallback((
    nextValues: Partial<QueryFilterValues<TSchema>>
      | ((current: QueryFilterValues<TSchema>) => Partial<QueryFilterValues<TSchema>>),
    updateOptions: QueryFilterUpdateOptions = {},
  ) => {
    updateParams((currentValues, nextParams) => {
      const patch = typeof nextValues === 'function'
        ? nextValues(currentValues)
        : nextValues;

      Object.entries(patch).forEach(([property, value]) => {
        const codec = schema[property];
        if (codec) {
          writeValue(nextParams, property, codec, value);
        }
      });

      const pageKey = options.pageKey;
      if (
        pageKey
        && !Object.prototype.hasOwnProperty.call(patch, pageKey)
        && updateOptions.resetPage !== false
      ) {
        const pageCodec = schema[pageKey];
        writeValue(nextParams, String(pageKey), pageCodec, pageCodec.defaultValue);
      }
    }, updateOptions);
  }, [options.pageKey, schema, updateParams]);

  const resetFilters = useCallback((updateOptions: QueryFilterUpdateOptions = {}) => {
    updateParams((_currentValues, nextParams) => {
      Object.entries(schema).forEach(([property, codec]) => {
        nextParams.delete(resolveParamName(property, codec));
      });
    }, {
      ...updateOptions,
      resetPage: false,
    });
  }, [schema, updateParams]);

  return {
    filters: values,
    setFilter,
    setFilters,
    resetFilters,
  } as const;
};

